import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useLanguage } from '../../../../hooks/useLanguage';
import { common as frCommon } from '../../../../locales/fr/common';
import { common as esCommon } from '../../../../locales/es/common';
import { common as arCommon } from '../../../../locales/ar/common';

const ResponsiveView = ({ data, columns, rowActions, collapsibleColumns }) => {
  const [expandedCards, setExpandedCards] = useState({});
  const { lang } = useLanguage();
  const translations = {
    fr: frCommon,
    es: esCommon,
    ar: arCommon
  };

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[lang];
    for (const k of keys) {
      value = value[k];
    }
    return value;
  };

  const toggleCard = (index) => {
    setExpandedCards(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const ChevronIcon = ({ isExpanded }) => (
    <svg
      className={`w-5 h-5 transform transition-transform duration-200 ease-in-out ${
        isExpanded ? 'rotate-180' : ''
      }`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 9l-7 7-7-7"
      />
    </svg>
  );

  const renderAdditionalData = (item) => {
    if (!collapsibleColumns) return null;
    
    return (
      <div className="bg-gray-50 p-4 border-t transition-all duration-300 ease-in-out">
        <div className="space-y-4">
          {collapsibleColumns.map((section) => (
            <div key={section.key} className="space-y-2">
              <h4 className="font-medium text-gray-900">{section.label}</h4>
              {section.render(item)}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
      {data.map((item, index) => (
        <div
          key={index}
          className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-200"
        >
          {/* Card Header */}
          <div className="p-4 border-b">
            <div className="flex justify-between items-start">
              <h3 className="text-lg font-semibold text-gray-800">
                {item[columns[0].key]}
              </h3>
              <div className="flex gap-2">
                {rowActions?.map((action, actionIndex) => (
                  <button
                    key={actionIndex}
                    onClick={() => action.onClick(item)}
                    className={`p-1 rounded-full ${action.className || 'text-gray-500 hover:text-gray-700'} transition-colors duration-200`}
                    title={t(`actions.${action.icon}`)}
                  >
                    {action.icon === 'edit' && (
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    )}
                    {action.icon === 'delete' && (
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    )}
                  </button>
                ))}
                <button
                  onClick={() => toggleCard(index)}
                  className="p-1 rounded-full hover:bg-gray-200 transition-colors duration-200"
                  title={expandedCards[index] ? t('actions.collapse') : t('actions.expand')}
                >
                  <ChevronIcon isExpanded={expandedCards[index]} />
                </button>
              </div>
            </div>
          </div>

          {/* Card Content - Main Columns */}
          <div className="p-4 space-y-2">
            {columns.slice(1).map((column, colIndex) => (
              <div key={colIndex} className="flex items-center">
                <span className="font-semibold w-1/3 text-sm text-gray-600">{column.label}:</span>
                <span className="w-2/3 text-sm">
                  {column.render ? column.render(item) : item[column.key]}
                </span>
              </div>
            ))}
          </div>

          {/* Collapsible Content */}
          <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
            expandedCards[index] ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          }`}>
            <div className="overflow-y-auto max-h-96">
              {renderAdditionalData(item)}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

ResponsiveView.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      render: PropTypes.func
    })
  ).isRequired,
  rowActions: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      title: PropTypes.string.isRequired,
      className: PropTypes.string
    })
  ),
  collapsibleColumns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      render: PropTypes.func.isRequired
    })
  )
};

export default ResponsiveView; 